import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Camera, X, Image as ImageIcon, Plus } from "lucide-react";
import { toast } from "sonner";

const PhotoCapture = ({ photos = [], captions = [], onPhotosChange, onCaptionsChange, maxPhotos = 10 }) => {
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const processFiles = async (files) => {
    if (files.length === 0) return;

    if (photos.length + files.length > maxPhotos) {
      toast.error(`Maximum ${maxPhotos} photos autorisées`);
      return;
    }

    setIsProcessing(true);
    try {
      const newPhotos = await Promise.all(
        files.map((file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              // Compress image
              const img = new window.Image();
              img.onload = () => {
                const canvas = document.createElement("canvas");
                const maxSize = 1200;
                let width = img.width;
                let height = img.height;

                if (width > height && width > maxSize) {
                  height = (height * maxSize) / width;
                  width = maxSize;
                } else if (height > maxSize) {
                  width = (width * maxSize) / height;
                  height = maxSize;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, width, height);
                
                resolve(canvas.toDataURL("image/jpeg", 0.8));
              };
              img.onerror = reject;
              img.src = e.target.result;
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        })
      );

      onPhotosChange([...photos, ...newPhotos]);
      // Add empty captions for new photos
      if (onCaptionsChange) {
        const newCaptions = [...captions, ...newPhotos.map(() => "")];
        onCaptionsChange(newCaptions);
      }
      toast.success(`${newPhotos.length} photo(s) ajoutée(s)`);
    } catch (error) {
      console.error("Error processing photos:", error);
      toast.error("Erreur lors du traitement des photos");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    await processFiles(files);
    if (event.target) event.target.value = "";
  };

  const handleCameraCapture = async (event) => {
    const files = Array.from(event.target.files);
    await processFiles(files);
    if (event.target) event.target.value = "";
  };

  const removePhoto = (index) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
    if (onCaptionsChange) {
      const newCaptions = captions.filter((_, i) => i !== index);
      onCaptionsChange(newCaptions);
    }
    toast.success("Photo supprimée");
  };

  const updateCaption = (index, value) => {
    if (onCaptionsChange) {
      const newCaptions = [...captions];
      newCaptions[index] = value;
      onCaptionsChange(newCaptions);
    }
  };

  return (
    <div className="space-y-4" data-testid="photo-capture">
      {/* Quick camera button - BIG and prominent */}
      <Button
        type="button"
        onClick={() => cameraInputRef.current?.click()}
        disabled={isProcessing || photos.length >= maxPhotos}
        className="w-full h-20 bg-sky-600 hover:bg-sky-700 text-white text-lg font-semibold"
        data-testid="quick-camera-btn"
      >
        {isProcessing ? (
          <div className="spinner w-8 h-8 border-white border-t-transparent" />
        ) : (
          <>
            <Camera className="w-8 h-8 mr-3" />
            Prendre une photo
          </>
        )}
      </Button>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCameraCapture}
        className="hidden"
      />

      {/* Secondary: from gallery */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-slate-700">
          Photos ({photos.length}/{maxPhotos})
        </label>
        <Button
          type="button"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={isProcessing || photos.length >= maxPhotos}
          className="h-10 px-3 text-sm"
          data-testid="add-photo-gallery-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Galerie
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {photos.length > 0 ? (
        <div className="space-y-4">
          <div className="photo-grid">
            {photos.map((photo, index) => (
              <div key={index} className="space-y-2">
                <div className="photo-item">
                  <img src={photo} alt={`Photo ${index + 1}`} />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 shadow-md hover:bg-red-600 active:scale-95"
                    data-testid={`remove-photo-${index}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <span className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                    {index + 1}
                  </span>
                </div>
                <input
                  type="text"
                  value={captions[index] || ""}
                  onChange={(e) => updateCaption(index, e.target.value)}
                  placeholder="Légende / description de la photo"
                  className="w-full text-xs px-2 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-400 placeholder:text-slate-400"
                  data-testid={`photo-caption-${index}`}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
          <ImageIcon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">Aucune photo</p>
          <p className="text-slate-400 text-xs mt-1">Utilisez le bouton ci-dessus pour photographier le sinistre</p>
        </div>
      )}
    </div>
  );
};

export default PhotoCapture;
